<script setup>
import { reactive, ref, watch, computed, onUnmounted } from "vue"
import VueDatePicker from "@vuepic/vue-datepicker"
import "@vuepic/vue-datepicker/dist/main.css"
import { useDuracionInput } from "@/composables/useDuracionInput"
import { useFormatters } from "@/composables/useFormatters"
import { useGoogleFlightsAutofill } from "@/composables/useGoogleFlightsAutofill"
import Swal from "sweetalert2"
import { es } from "date-fns/locale"
const props = defineProps({

  visible: Boolean,

  multiDestino: Boolean,

  ida: Object,
  intermedio: Object,
  regreso: Object,

  precioSimple: [String, Number],

  preciosMulti: Object, 

  fechaIda: String,
  fechaRegreso: String

})

const emit = defineEmits([
  "close",
  "guardar",
  "update:multiDestino",
  "update:precioSimple"
])

const {
  validarYFormatearDuracion,
  bloquearDuracion
} = useDuracionInput()

const {
  formatoCOP
} = useFormatters()

// =========================
// AUTOCOMPLETAR DESDE GOOGLE FLIGHTS
// =========================

// Una instancia del composable por cada input de URL — cada una trae su
// propio "loading" independiente. Con una sola instancia compartida (como
// estaba antes) los 4 botones mostraban "Buscando..." a la vez apenas se
// usaba cualquiera de ellos, porque compartían el mismo ref.
const { tramoAFormulario } = useGoogleFlightsAutofill()

const {
  loading: cargandoCombinado,
  parsearUrl: parsearUrlCombinado
} = useGoogleFlightsAutofill()

const {
  loading: cargandoIda,
  parsearUrl: parsearUrlIda
} = useGoogleFlightsAutofill()

const {
  loading: cargandoIntermedio,
  parsearUrl: parsearUrlIntermedio
} = useGoogleFlightsAutofill()

const {
  loading: cargandoRegreso,
  parsearUrl: parsearUrlRegreso
} = useGoogleFlightsAutofill()

const urlGoogleFlights = ref("")

// Modo "ida y vuelta" (2 tramos): se pega UNA sola URL combinada — Google
// Flights la arma como un único resultado con precio total conjunto, así
// que ese precio total se vuelca completo al campo "Precio total".
async function autocompletarDesdeUrl() {
  const url = urlGoogleFlights.value.trim()
  if (!url) return

  const resultado = await parsearUrlCombinado(url)
  if (!resultado) return
  const { tramos, precio } = resultado

  if (tramos.length !== 2) {
    Swal.fire({
      icon: "warning",
      title: "Esta URL no es de ida y vuelta simple",
      text: "Trae más o menos de 2 tramos. Si es un viaje multidestino, activa el switch \"Multidestino\" y pega cada tramo por separado en su propio campo (ida, intermedio, regreso)."
    })
    return
  }

  const nuevoIda = tramoAFormulario(tramos[0])
  if (nuevoIda) form.ida = { escalas: [], tipo: "directo", diasExtra: 0, ...nuevoIda }

  const nuevoRegreso = tramoAFormulario(tramos[1])
  if (nuevoRegreso) form.regreso = { escalas: [], tipo: "directo", diasExtra: 0, ...nuevoRegreso }

  if (precio != null) {
    form.precioSimple = String(precio)
  }

  urlGoogleFlights.value = ""
}

// Modo "multidestino" (3 tramos): a diferencia del combinado de arriba,
// cada tramo se pega por separado — la URL de un vuelo SENCILLO (un solo
// tramo, ida nada más) ya seleccionado en Google Flights para ese tramo
// puntual. Así cada uno trae su propio precio real, en vez de repartir a
// ciegas el total de una búsqueda de "varias ciudades" que Google no
// desglosa por tramo.
const urlIda = ref("")
const urlIntermedio = ref("")
const urlRegreso = ref("")

const DEFAULTS_TRAMO = {
  ida: { escalas: [], tipo: "directo", diasExtra: 0 },
  intermedio: { escalas: [], tipo: "directo" },
  regreso: { escalas: [], tipo: "directo", diasExtra: 0 }
}

async function autocompletarTramo(urlRef, destino, parsear) {
  const url = urlRef.value.trim()
  if (!url) return

  const resultado = await parsear(url)
  if (!resultado) return
  const { tramos, precio } = resultado

  if (tramos.length !== 1) {
    Swal.fire({
      icon: "warning",
      title: "Esta URL no es de un solo tramo",
      text: "Pega acá la URL de un vuelo sencillo (un solo tramo, ida nada más) ya seleccionado en Google Flights, no una búsqueda de varias ciudades."
    })
    return
  }

  const nuevoTramo = tramoAFormulario(tramos[0])
  if (nuevoTramo) form[destino] = { ...DEFAULTS_TRAMO[destino], ...nuevoTramo }

  if (precio != null) {
    form.preciosMulti[destino] = String(precio)
  }

  urlRef.value = ""
}

const autocompletarIda = () => autocompletarTramo(urlIda, "ida", parsearUrlIda)
const autocompletarIntermedio = () => autocompletarTramo(urlIntermedio, "intermedio", parsearUrlIntermedio)
const autocompletarRegreso = () => autocompletarTramo(urlRegreso, "regreso", parsearUrlRegreso)

// =========================
// LOCAL STATE
// =========================

const form = reactive({

  ida: {},

  intermedio: {},

  regreso: {},

  preciosMulti: {},

  precioSimple: ""

})

watch(
  () => form.precioSimple,
  (value) => {
    emit("update:precioSimple", value)
  }
)

// =========================
// SYNC PROPS -> LOCAL
// =========================

watch(
  () => props.visible,
  () => {

    form.ida = {
      escalas: [],
      tipo: "directo",
      diasExtra: 0,
      ...structuredClone(props.ida || {})
    }

    form.intermedio = {
      escalas: [],
      tipo: "directo",
      ...structuredClone(props.intermedio || {})
    }

    form.regreso = {
      escalas: [],
      tipo: "directo",
      diasExtra: 0,
      ...structuredClone(props.regreso || {})
    }

    form.preciosMulti = structuredClone(
      props.preciosMulti || {
        ida: "",
        intermedio: "",
        regreso: ""
      }
    )

    form.precioSimple = props.precioSimple || ""

  },
  {
    immediate: true
  }
)

// =========================
// WATCH ESCALAS
// =========================

watch(
  () => form.ida.tipo,
  (val) => {

    if (val === "con escalas") {

      if (!form.ida.escalas?.length) {
        actualizarNumeroEscalas(form.ida, 1)
      }

    } else {

      form.ida.escalas = []

    }

  }
)

watch(
  () => form.intermedio.tipo,
  (val) => {

    if (val === "con escalas") {

      if (!form.intermedio.escalas?.length) {
        actualizarNumeroEscalas(form.intermedio, 1)
      }

    } else {

      form.intermedio.escalas = []

    }

  }
)

watch(
  () => form.regreso.tipo,
  (val) => {

    if (val === "con escalas") {

      if (!form.regreso.escalas?.length) {
        actualizarNumeroEscalas(form.regreso, 1)
      }

    } else {

      form.regreso.escalas = []

    }

  }
)

// =========================
// PRECIO TOTAL
// =========================

const precioTotalMulti = computed(() => {

  return (
    Number(form.preciosMulti.ida || 0) +
    Number(form.preciosMulti.intermedio || 0) +
    Number(form.preciosMulti.regreso || 0)
  )

})

// =========================
// HELPERS
// =========================

function actualizarDuracion(vuelo, value) {

  vuelo.duracion =
    validarYFormatearDuracion(value)

}

function actualizarPrecioMulti(key, value) {

  form.preciosMulti[key] =
    value.replace(/\D/g, "")

}

function actualizarNumeroEscalas(vuelo, cantidad) {

  const total = Number(cantidad)

  if (!vuelo.escalas) {
    vuelo.escalas = []
  }

  while (vuelo.escalas.length < total) {

    vuelo.escalas.push({
      duracion: "",
      nombre: ""
    })

  }

  while (vuelo.escalas.length > total) {
    vuelo.escalas.pop()
  }

}

// =========================
// GUARDAR
// =========================

function handleGuardar() {

  const payload = {

    precio: props.multiDestino
      ? precioTotalMulti.value
      : form.precioSimple,

    ida: structuredClone(form.ida),

    intermedio: props.multiDestino
      ? structuredClone(form.intermedio)
      : null,

    regreso: structuredClone(form.regreso),

    preciosMulti: structuredClone(form.preciosMulti),

    precioSimple: form.precioSimple

  }

  emit("guardar", payload)

}

watch(
  () => props.visible,
  (visible) => {
    document.body.style.overflow = visible ? "hidden" : "";
  }
);

onUnmounted(() => {
  document.body.style.overflow = "";
});
</script>

<template>
  <template v-if="visible">

    <!-- Fondo oscuro -->
    <div class="modal-backdrop-custom"></div>
    <div v-if="visible" class="modal fade show d-block modal-manual" tabindex="-1">

      <div class="modal-dialog modal-xl modal-dialog-centered">

        <div class="modal-content bg-dark text-white">

          <!-- HEADER -->
          <div class="modal-header">

            <h5 class="modal-title">
              Ingresar Vuelo Manual
            </h5>

            <button class="btn-close btn-close-white" @click="$emit('close')" />

          </div>

          <!-- BODY -->
          <div class="modal-body">

            <form @submit.prevent="handleGuardar">

              <!-- AUTOCOMPLETAR DESDE GOOGLE FLIGHTS (ida y vuelta: 1 URL combinada) -->
              <div v-if="!multiDestino" class="card bg-secondary border-0 mb-4">
                <div class="card-body">
                  <label class="form-label small mb-1">
                    Pegar URL de Google Flights (del vuelo seleccionado)
                  </label>
                  <div class="d-flex gap-2">
                    <input v-model="urlGoogleFlights" type="text" class="form-control"
                      placeholder="https://www.google.com/travel/flights/booking?tfs=..."
                      @keydown.enter.prevent="autocompletarDesdeUrl">
                    <button type="button" class="btn btn-info text-nowrap" :disabled="cargandoCombinado"
                      @click="autocompletarDesdeUrl">
                      {{ cargandoCombinado ? "Buscando..." : "Autocompletar" }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- TIPO -->
              <div class="mb-4 text-center">

                <label class="form-label d-block mb-2">
                  Tipo de viaje
                </label>

                <div class="d-flex justify-content-center align-items-center gap-3">

                  <span class="fw-bold text-info">
                    Ida y regreso
                  </span>

                  <label class="switch">

                    <input type="checkbox" class="form-check-input" :checked="multiDestino"
                      @change="$emit('update:multiDestino', $event.target.checked)" />

                    <span class="slider"></span>

                  </label>

                  <span class="text-secondary">
                    Multidestino
                  </span>

                </div>

              </div>

              <!-- IDA -->
              <div class="card bg-secondary border-0 mb-4">

                <div class="card-header bg-primary">
                  Vuelo IDA
                </div>

                <div class="card-body">

                  <!-- AUTOCOMPLETAR IDA (multidestino: URL de un solo tramo) -->
                  <div v-if="multiDestino" class="mb-3">
                    <label class="form-label small mb-1">
                      Pegar URL de Google Flights (vuelo sencillo de ida)
                    </label>
                    <div class="d-flex gap-2">
                      <input v-model="urlIda" type="text" class="form-control"
                        placeholder="https://www.google.com/travel/flights/booking?tfs=..."
                        @keydown.enter.prevent="autocompletarIda">
                      <button type="button" class="btn btn-info text-nowrap" :disabled="cargandoIda"
                        @click="autocompletarIda">
                        {{ cargandoIda ? "Buscando..." : "Autocompletar" }}
                      </button>
                    </div>
                  </div>

                  <div class="row g-3">

                    <div class="col-md-3">
                      <label class="form-label">Aerolínea</label>
                      <input v-model="form.ida.aerolinea" class="form-control" />
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Salida</label>
                      <VueDatePicker v-model="form.ida.salida" time-picker is-24 format="HH:mm" model-type="HH:mm"
                        auto-apply :clearable="false" />
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Llegada</label>

                      <VueDatePicker v-model="form.ida.llegada" time-picker is-24 format="HH:mm" model-type="HH:mm"
                        auto-apply :clearable="false" />

                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Dia Llegada</label>

                      <select v-model.number="form.ida.diasExtra" class="form-select">
                        <option :value="0">Mismo día</option>
                        <option :value="1">+1 día</option>
                        <option :value="2">+2 días</option>
                      </select>
                    </div>

                    <div class="col-md-3 d-none">
                      <label class="form-label">Fecha</label>
                      <input type="date" :value="fechaIda" class="form-control" disabled />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Duración</label>

                      <input :value="form.ida.duracion" @keydown="bloquearDuracion"
                        @input="actualizarDuracion(form.ida, $event.target.value)" class="form-control"
                        placeholder="Ej: 5h 30m" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Tipo</label>

                      <select v-model="form.ida.tipo" class="form-select">

                        <option value="directo">
                          Directo
                        </option>

                        <option value="con escalas">
                          Con escala
                        </option>

                      </select>
                    </div>

                  </div>

                  <!-- ESCALAS IDA -->
                  <div v-if="form.ida.tipo === 'con escalas'" class="mt-4">

                    <div class="row mb-3">

                      <div class="col-md-12">

                        <label class="form-label">
                          Número de escalas
                        </label>

                        <input type="number" min="1" max="5" class="form-control" :value="form.ida.escalas?.length || 1"
                          @input="actualizarNumeroEscalas(form.ida, $event.target.value)" />

                      </div>

                    </div>

                    <div v-for="(escala, index) in form.ida.escalas" :key="index" class="row g-3 mb-3 border-top pt-3">

                      <div class="col-md-4">

                        <label class="form-label">
                          Tiempo escala
                        </label>

                        <input :value="escala.duracion" @keydown="bloquearDuracion"
                          @input="escala.duracion = validarYFormatearDuracion($event.target.value)" class="form-control"
                          placeholder="Ej: 2h 30m" />

                      </div>

                      <div class="col-md-8">

                        <label class="form-label">
                          Nombre escala
                        </label>

                        <input type="text" class="form-control" placeholder="Ej: Aeropuerto El Dorado"
                          v-model="escala.nombre" />

                      </div>

                    </div>

                  </div>

                </div>

              </div>

              <!-- INTERMEDIO -->
              <div v-if="multiDestino" class="card bg-secondary border-0 mb-4">

                <div class="card-header bg-info">
                  Vuelo INTERMEDIO
                </div>

                <div class="card-body">

                  <!-- AUTOCOMPLETAR INTERMEDIO (URL de un solo tramo) -->
                  <div class="mb-3">
                    <label class="form-label small mb-1">
                      Pegar URL de Google Flights (vuelo sencillo intermedio)
                    </label>
                    <div class="d-flex gap-2">
                      <input v-model="urlIntermedio" type="text" class="form-control"
                        placeholder="https://www.google.com/travel/flights/booking?tfs=..."
                        @keydown.enter.prevent="autocompletarIntermedio">
                      <button type="button" class="btn btn-info text-nowrap" :disabled="cargandoIntermedio"
                        @click="autocompletarIntermedio">
                        {{ cargandoIntermedio ? "Buscando..." : "Autocompletar" }}
                      </button>
                    </div>
                  </div>

                  <div class="row g-3">

                    <div class="col-md-3">
                      <label class="form-label">Aerolínea</label>

                      <input v-model="form.intermedio.aerolinea" class="form-control" />
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Salida</label>
                      <VueDatePicker v-model="form.intermedio.salida" time-picker is-24 format="HH:mm"
                        model-type="HH:mm" auto-apply :clearable="false" />

                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Llegada</label>
                      <VueDatePicker v-model="form.intermedio.llegada" time-picker is-24 format="HH:mm"
                        model-type="HH:mm" auto-apply :clearable="false" />

                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Fecha intermedia</label>

                      <VueDatePicker v-model="form.intermedio.fecha" :format-locale="es" locale="es"
                        model-type="yyyy-MM-dd" format="yyyy-MM-dd" auto-apply :enable-time-picker="false"
                        :clearable="false" :min-date="fechaIda" :max-date="fechaRegreso" />


                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Duración</label>

                      <input :value="form.intermedio.duracion" @keydown="bloquearDuracion"
                        @input="actualizarDuracion(form.intermedio, $event.target.value)" class="form-control"
                        placeholder="Ej: 5h 30m" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Tipo</label>

                      <select v-model="form.intermedio.tipo" class="form-select">

                        <option value="directo">
                          Directo
                        </option>

                        <option value="con escalas">
                          Con escala
                        </option>

                      </select>
                    </div>

                  </div>

                  <!-- ESCALAS INTERMEDIO -->
                  <div v-if="form.intermedio.tipo === 'con escalas'" class="mt-4">

                    <div class="row mb-3">

                      <div class="col-md-12">

                        <label class="form-label">
                          Número de escalas
                        </label>

                        <input type="number" min="1" max="5" class="form-control"
                          :value="form.intermedio.escalas?.length || 1"
                          @input="actualizarNumeroEscalas(form.intermedio, $event.target.value)" />

                      </div>

                    </div>

                    <div v-for="(escala, index) in form.intermedio.escalas" :key="index"
                      class="row g-3 mb-3 border-top pt-3">

                      <div class="col-md-4">

                        <label class="form-label">
                          Tiempo escala
                        </label>

                        <input :value="escala.duracion" @keydown="bloquearDuracion"
                          @input="escala.duracion = validarYFormatearDuracion($event.target.value)" class="form-control"
                          placeholder="Ej: 2h 30m" />

                      </div>

                      <div class="col-md-8">

                        <label class="form-label">
                          Nombre escala
                        </label>

                        <input type="text" class="form-control" placeholder="Ej: Aeropuerto El Dorado"
                          v-model="escala.nombre" />

                      </div>

                    </div>

                  </div>

                </div>

              </div>

              <!-- REGRESO -->
              <div class="card bg-secondary border-0 mb-4">

                <div class="card-header bg-success">
                  Vuelo REGRESO
                </div>

                <div class="card-body">

                  <!-- AUTOCOMPLETAR REGRESO (multidestino: URL de un solo tramo) -->
                  <div v-if="multiDestino" class="mb-3">
                    <label class="form-label small mb-1">
                      Pegar URL de Google Flights (vuelo sencillo de regreso)
                    </label>
                    <div class="d-flex gap-2">
                      <input v-model="urlRegreso" type="text" class="form-control"
                        placeholder="https://www.google.com/travel/flights/booking?tfs=..."
                        @keydown.enter.prevent="autocompletarRegreso">
                      <button type="button" class="btn btn-info text-nowrap" :disabled="cargandoRegreso"
                        @click="autocompletarRegreso">
                        {{ cargandoRegreso ? "Buscando..." : "Autocompletar" }}
                      </button>
                    </div>
                  </div>

                  <div class="row g-3">

                    <div class="col-md-3">
                      <label class="form-label">Aerolínea</label>
                      <input v-model="form.regreso.aerolinea" class="form-control" />
                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Salida</label>
                      <VueDatePicker v-model="form.regreso.salida" time-picker is-24 format="HH:mm" model-type="HH:mm"
                        auto-apply :clearable="false" />



                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Llegada</label>
                      <VueDatePicker v-model="form.regreso.llegada" time-picker is-24 format="HH:mm" model-type="HH:mm"
                        auto-apply :clearable="false" />

                    </div>

                    <div class="col-md-3">
                      <label class="form-label">Dia Llegada</label>

                      <select v-model.number="form.regreso.diasExtra" class="form-select">
                        <option :value="0">Mismo día</option>
                        <option :value="1">+1 día</option>
                        <option :value="2">+2 días</option>
                      </select>
                    </div>

                    <div class="col-md-3 d-none">
                      <label class="form-label">Fecha</label>
                      <input type="date" :value="fechaRegreso" class="form-control" disabled />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Duración</label>

                      <input :value="form.regreso.duracion" @keydown="bloquearDuracion"
                        @input="actualizarDuracion(form.regreso, $event.target.value)" class="form-control" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">Tipo</label>

                      <select v-model="form.regreso.tipo" class="form-select">

                        <option value="directo">
                          Directo
                        </option>

                        <option value="con escalas">
                          Con escala
                        </option>

                      </select>
                    </div>

                  </div>

                  <!-- ESCALAS REGRESO -->
                  <div v-if="form.regreso.tipo === 'con escalas'" class="mt-4">

                    <div class="row mb-3">

                      <div class="col-md-12">

                        <label class="form-label">
                          Número de escalas
                        </label>

                        <input type="number" min="1" max="5" class="form-control"
                          :value="form.regreso.escalas?.length || 1"
                          @input="actualizarNumeroEscalas(form.regreso, $event.target.value)" />

                      </div>

                    </div>

                    <div v-for="(escala, index) in form.regreso.escalas" :key="index"
                      class="row g-3 mb-3 border-top pt-3">

                      <div class="col-md-4">

                        <label class="form-label">
                          Tiempo escala
                        </label>

                        <input :value="escala.duracion" @keydown="bloquearDuracion"
                          @input="escala.duracion = validarYFormatearDuracion($event.target.value)" class="form-control"
                          placeholder="Ej: 2h 30m" />

                      </div>

                      <div class="col-md-8">

                        <label class="form-label">
                          Nombre escala
                        </label>

                        <input type="text" class="form-control" placeholder="Ej: Aeropuerto El Dorado"
                          v-model="escala.nombre" />

                      </div>

                    </div>

                  </div>

                </div>

              </div>

              <!-- PRECIOS -->
              <div class="card bg-secondary border-0">

                <div class="card-header bg-warning text-dark">

                  <i class="bi bi-cash-coin"></i>
                  Precio

                </div>

                <div class="card-body">

                  <!-- SIMPLE -->
                  <div v-if="!multiDestino">

                    <label class="form-label">
                      Precio total
                    </label>

                    <input :value="formatoCOP(form.precioSimple)"
                      @input="form.precioSimple = $event.target.value.replace(/\D/g, '')" class="form-control" />

                  </div>

                  <!-- MULTI -->
                  <div v-else class="row">

                    <div class="col-md-4">

                      <label class="form-label">
                        Ida
                      </label>

                      <input :value="formatoCOP(form.preciosMulti.ida)"
                        @input="actualizarPrecioMulti('ida', $event.target.value)" class="form-control" />

                    </div>

                    <div class="col-md-4">

                      <label class="form-label">
                        Intermedio
                      </label>

                      <input :value="formatoCOP(form.preciosMulti.intermedio)"
                        @input="actualizarPrecioMulti('intermedio', $event.target.value)" class="form-control" />

                    </div>

                    <div class="col-md-4">

                      <label class="form-label">
                        Regreso
                      </label>

                      <input :value="formatoCOP(form.preciosMulti.regreso)"
                        @input="actualizarPrecioMulti('regreso', $event.target.value)" class="form-control" />

                    </div>

                    <div class="col-12 mt-4 text-center">

                      <h4 class="text-white">

                        Total:
                        {{ formatoCOP(precioTotalMulti) }}

                      </h4>

                    </div>

                  </div>

                </div>

              </div>

              <!-- BOTONES -->
              <div class="d-flex gap-3 mt-4">

                <button type="button" class="btn btn-outline-light w-50" @click="$emit('close')">
                  Cancelar
                </button>

                <button type="submit" class="btn btn-success w-50">
                  Guardar vuelo
                </button>

              </div>

            </form>

          </div>

        </div>

      </div>

    </div>
  </template>
</template>
<style>
.modal-manual {
  position: fixed;
  z-index: 999999 !important;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 999998;
}

.modal-manual .modal-dialog {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

/* Toggle "Tipo de viaje" (Ida y regreso / Multidestino) — el markup ya
   traía label.switch > input[checkbox] + span.slider, pero sin CSS caía a
   un checkbox nativo en vez de un slide toggle. */
.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch .slider {
  position: absolute;
  inset: 0;
  cursor: pointer;
  background-color: #6c757d;
  border-radius: 24px;
  transition: background-color 0.2s;
}

.switch .slider::before {
  content: "";
  position: absolute;
  left: 3px;
  bottom: 3px;
  width: 18px;
  height: 18px;
  background-color: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
}

.switch input:checked + .slider {
  background-color: #0dcaf0;
}

.switch input:checked + .slider::before {
  transform: translateX(22px);
}

.switch input:focus-visible + .slider {
  outline: 2px solid #0dcaf0;
  outline-offset: 2px;
}
</style>
